import Layout from "../layouts/Layout"
import { Head } from "@inertiajs/react"
import { ReactNode } from "react"
import { Link } from "@inertiajs/react"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Badge } from "../components/ui/badge"
import { ArrowRight, Code, Zap, Globe, Fingerprint } from "lucide-react"
import { usePage } from "@inertiajs/react"
import { Project } from "../types/project"
import { BlogPost } from "../types/blog"

declare module "@inertiajs/core" {
  interface PageProps {
    featured_projects: Project[]
    recent_posts: BlogPost[]
  }
}

const Index = () => {
  const { featured_projects, recent_posts } = usePage().props

  return (
    <>
      <Head title="Codeware - Web Development and AI Solutions" >
        <meta name="description" content="Codeware is my development company specializing in Ruby on Rails development and AI integration. We build scalable applications and provide domain industry solutions." />
      </Head>

      <div className="flex flex-col min-h-screen">
        {/* Hero Section */}
        <section className="relative py-20 sm:py-32 bg-gradient-to-br from-background via-background to-muted/20">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl sm:text-6xl font-bold tracking-tight mb-6">
                Web Development
                <span className="block text-primary">& AI Solutions</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Delivering enterprise-grade <a href="https://rubyonrails.org/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Ruby on Rails</a> applications and AI-powered solutions. 
                Scalable web development, machine learning integration and domain industry expertise.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" asChild>
                  <Link href="/projects">View Projects <ArrowRight className="ml-2 h-4 w-4" /></Link>
                </Button>
                <Button size="lg" variant="outline" asChild>
                  <Link href="/contact">Get In Touch</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold mb-4">Services</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Professional development services combining modern technology with industry expertise
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card>
                <CardHeader>
                  <Code className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Rails Development</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Full-stack Rails applications built with modern architecture and industry best practices.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <Zap className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">AI Integration</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    AI-powered features: OpenAI integration, MCP servers, NLP, analytics, and automation.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <Globe className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Domain Industry Expertise</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    Specialized solutions for domain registry operations and DNS infrastructure.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardHeader>
                  <Fingerprint className="h-8 w-8 text-primary mb-2" />
                  <CardTitle className="text-lg">Strong Authentication</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    WebAuthn/FIDO2 passkeys, passwordless, Smart‑ID/Mobile‑ID, and ID‑card integrations.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Recent Blog Posts */}
        <section className="py-16">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl font-bold mb-4">Latest Insights</h2>
                <p className="text-muted-foreground">
                  Technical articles on Rails, AI, and domain industry
                </p>
              </div>
              <Button variant="outline" asChild>
                <Link href="/blog">View All Posts</Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {recent_posts.map((post) => (
                <Card key={post.slug} className="hover:shadow-lg transition-shadow">
                  <CardHeader>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {post.tags_string?.split(',').map((tag) => (
                        <Badge key={tag} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <CardTitle className="text-lg line-clamp-2">
                      <Link href={`/blog/${post.slug}`} className="hover:text-primary transition-colors">
                        {post.title}
                      </Link>
                    </CardTitle>
                    <CardDescription>{new Date(post.date).toLocaleDateString()}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {post.excerpt}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center mb-12">
              <div>
                <h2 className="text-3xl font-bold mb-4">Featured Projects</h2>
                <p className="text-muted-foreground">
                  Showcase of work combining Rails, AI, and domain expertise
                </p>
              </div>
              <Button variant="outline" asChild>
                <Link href="/projects">View All Projects</Link>
              </Button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {featured_projects.map((project) => (
                <Card key={project.title} className="hover:shadow-lg transition-shadow">
                  <div className="aspect-video bg-muted rounded-t-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={project.image?.attached?.[0]?.url || project.image_url || 'placeholder.png'} 
                      alt={project.title}
                      className="w-full h-full object-contain rounded-t-lg"
                    />
                  </div>
                  <CardHeader>
                    <CardTitle className="text-lg">{project.title}</CardTitle>
                    <CardDescription>{project.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex flex-wrap gap-2">
                      {project.tech_stack.split(',').map((tech) => (
                        <Badge key={tech} variant="outline" className="text-xs">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

Index.layout = (page: ReactNode) => <Layout>{page}</Layout>

export default Index